import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Ship, HeartPulse } from "lucide-react";
import { Reveal, MaskedLines, Stagger, StaggerItem } from "@/components/site/Reveal";

const scope = [
  "Medical Examination Process", "Doctor Assessment", "Medical Equipment", "Calibration Records",
  "Laboratory Quality", "Report Accuracy", "Medical Documentation", "Record Keeping",
  "Data Security", "Candidate Consent", "Medical Guidelines", "Turnaround Time",
];

const deliverables = [
  "Clinic Audit Report", "Compliance Scorecard", "Risk Assessment",
  "Corrective & Preventive Action (CAPA)", "Improvement Recommendations",
  "Follow-up Audit", "Annual Compliance Certificate",
];

const shipBenefits = [
  "Consistent clinic quality", "Reduced medical errors", "Reduced repatriation",
  "Better compliance", "Independent clinic assessment", "Continuous quality improvement",
];

const clinicBenefits = [
  "Performance benchmarking", "Process improvements", "Regulatory compliance",
  "Quality enhancement", "Improved client confidence",
];

export default function ClinicAudit() {
  return (
    <main data-testid="audit-page" className="bg-[#f7f4ee] overflow-x-clip">
      {/* HERO */}
      <section className="relative pt-40 md:pt-52 pb-16 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#12294d] to-[#0c1d3a]" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="overline-label mb-6">
            Service — Quality Audits
          </motion.p>
          <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.12] max-w-4xl" data-testid="audit-heading">
            <MaskedLines
              lines={[
                <>Clinic Quality Audit &</>,
                <><span className="text-[#2ab5cf]">Compliance</span> Program</>,
              ]}
              startDelay={0.15}
            />
          </h1>
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="mt-8 text-lg md:text-xl text-slate-200 max-w-2xl leading-relaxed">
            Ensuring quality across every approved medical clinic in your global network.
          </motion.p>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Reveal>
            <h2 className="font-display text-[#12294d] text-3xl md:text-4xl tracking-tight leading-tight" data-testid="audit-intro-heading">
              Ensuring Quality Across Every Approved Medical Clinic
            </h2>
            <div className="mt-8 space-y-5 text-slate-600 leading-relaxed text-base md:text-lg">
              <p>
                A high-quality medical network requires more than qualified doctors—it requires consistent
                processes, standardized documentation and continuous compliance.
              </p>
              <p>
                MedVerify conducts structured audits to ensure each approved clinic meets the medical and
                operational standards expected by ship management companies.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SCOPE */}
      <section className="py-16 md:py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="overline-label">Twelve areas, one standard</p>
            <h2 className="font-display text-[#12294d] text-4xl md:text-5xl tracking-tight mt-4">Scope of Audit</h2>
          </Reveal>
          <Stagger className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {scope.map((s, i) => (
              <StaggerItem key={i}>
                <div className="h-full bg-white border border-[#1a3a6b]/10 rounded-2xl p-6 shadow-lg shadow-[#1a3a6b]/5" data-testid={`audit-scope-${i}`}>
                  <p className="font-display text-2xl text-[#2ab5cf]/50">{String(i + 1).padStart(2, "0")}</p>
                  <p className="mt-3 text-sm font-semibold text-[#12294d]">{s}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* DELIVERABLES */}
      <section className="py-16 md:py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          <Reveal>
            <p className="overline-label">Every audit ends with an actionable outcome</p>
            <h2 className="font-display text-[#12294d] text-4xl md:text-5xl tracking-tight mt-4" data-testid="audit-deliverables-heading">Audit Deliverables</h2>
            <p className="mt-8 text-slate-600 leading-relaxed text-base md:text-lg">
              Our audits are not checklists — they are structured improvement programs backed by clear
              documentation, scoring and follow-up.
            </p>
          </Reveal>
          <Stagger className="space-y-4">
            {deliverables.map((d, i) => (
              <StaggerItem key={i}>
                <div className="flex items-center gap-4 bg-white border border-[#1a3a6b]/10 rounded-2xl px-6 py-5 shadow-lg shadow-[#1a3a6b]/5" data-testid={`audit-deliverable-${i}`}>
                  <span className="h-7 w-7 rounded-full bg-[#2ab5cf]/15 flex items-center justify-center shrink-0">
                    <Check size={14} strokeWidth={3} className="text-[#2ab5cf]" />
                  </span>
                  <p className="text-[#12294d] font-semibold text-sm">{d}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="overline-label">Value on both sides of the audit</p>
            <h2 className="font-display text-[#12294d] text-4xl md:text-5xl tracking-tight mt-4">Benefits</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Reveal delay={0.05}>
              <div className="bg-white border border-[#1a3a6b]/10 rounded-[2rem] p-10 h-full shadow-lg shadow-[#1a3a6b]/5" data-testid="audit-benefits-ship">
                <span className="h-20 w-20 rounded-full bg-[#1a3a6b]/10 flex items-center justify-center">
                  <Ship size={40} className="text-[#1a3a6b]" />
                </span>
                <h3 className="font-display text-[#12294d] text-2xl mt-8 tracking-tight">Ship Management Companies</h3>
                <ul className="mt-6 space-y-3.5">
                  {shipBenefits.map((b, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm font-medium text-slate-700">
                      <span className="mt-0.5 h-5 w-5 rounded-full bg-[#2ab5cf]/15 flex items-center justify-center shrink-0">
                        <Check size={12} strokeWidth={3} className="text-[#2ab5cf]" />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="bg-white border border-[#1a3a6b]/10 rounded-[2rem] p-10 h-full shadow-lg shadow-[#1a3a6b]/5" data-testid="audit-benefits-clinic">
                <span className="h-20 w-20 rounded-full bg-[#2ab5cf]/12 flex items-center justify-center">
                  <HeartPulse size={40} className="text-[#2ab5cf]" />
                </span>
                <h3 className="font-display text-[#12294d] text-2xl mt-8 tracking-tight">Medical Clinics</h3>
                <ul className="mt-6 space-y-3.5">
                  {clinicBenefits.map((b, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm font-medium text-slate-700">
                      <span className="mt-0.5 h-5 w-5 rounded-full bg-[#2ab5cf]/15 flex items-center justify-center shrink-0">
                        <Check size={12} strokeWidth={3} className="text-[#2ab5cf]" />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="rounded-[2rem] p-10 md:p-14 bg-white border border-[#1a3a6b]/10 shadow-xl shadow-[#1a3a6b]/5 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
              <div>
                <h2 className="font-display text-[#12294d] text-2xl md:text-3xl tracking-tight max-w-md">
                  See why our clients trust MedVerify PEME Review System
                </h2>
              </div>
              <Link to="/why-med-verify" data-testid="audit-cta-why" className="rounded-full px-8 py-4 text-sm font-bold inline-flex items-center gap-2 border border-[#1a3a6b]/25 text-[#1a3a6b] hover:bg-[#1a3a6b] hover:text-white transition-colors duration-300 shrink-0">
                Why Choose MedVerify <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
